import { Component } from "@angular/core";
import { ComponentInteractionService } from "src/app/services/component-interaction/component-interaction.service";
import { ColorsPicker } from "@utils/color-constants";
import { InteractionService } from "../../shared/helpers/interaction.service";
import { take } from "rxjs";
import { GenericDialogComponent } from "../../shared/generic-dialog/generic-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { CopyPasteService } from "../../services/drag-and-drop/copypaste.service";
import { SnackBarConfig } from "../../constants/snackbar.constants";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Action, MeshState, UndoRedoAction } from "../../shared/models/undoRedo.model";
import { MeshSelection } from "../../models/designer";
import { SceneManagerService } from "../../services/scene-manager/scene-manager.service";
import { MeshVisualService } from "../../services/designer/mesh-visual.service";
import { Object3DUserData } from "~/src/utils/shape";
import { UndoRedoService } from "../../services/undoRedo/undo-redo.service";
import { StructureResponseMessage } from "@models/structure.model";
import { CustomTourService } from "../../services/custom-tour.service";

@Component({
    selector: "colour-picker",
    templateUrl: "./colour-picker.component.html",
    styleUrls: ["./colour-picker.component.scss"],
    standalone: false,
})
export class ColourPickerComponent {
    public colorsPicker = ColorsPicker;
    public selectedColor: string = ColorsPicker[0].value;
    private selectionList: MeshSelection[] = [];
    public isStepTourActive: boolean = false;

    constructor(
        private componentInteractionService: ComponentInteractionService,
        private interactionService: InteractionService,
        private dialog: MatDialog,
        private copyPasteService: CopyPasteService,
        private snackBar: MatSnackBar,
        private translate: TranslateService,
        private meshVisualService: MeshVisualService,
        private scenemanagerService: SceneManagerService,
        private undoRedoService: UndoRedoService,
        private custmService: CustomTourService,
    ) {
        this.componentInteractionService.meshSelections$.subscribe((selections) => {
            this.selectionList = selections;
        });
    }

    ngOnInit(): void {
        /*** This will trigger when Step tour is Active ***/
        this.custmService.stepShow$.subscribe((response) => {
            this.isStepTourActive = true;
        });

        /*** This will trigger when the PoPup is close  ***/
        this.custmService.openActionsCall$.subscribe((response) => {
            this.isStepTourActive = false;
        });
    }

    // color should be of format #117A65
    public async changeColor(color: string) {
        this.selectedColor = color;
        if (this.copyPasteService.copiedMeshes.length) {
            this.openSnackBar(
                this.getTranslation("designer.invalidMessage.disableColorChangeAction"),
                SnackBarConfig.Actions.OKAY,
            );
            return;
        }
        this.interactionService.isSelected.pipe(take(1)).subscribe((res) => {
            if (!res) {
                this.componentInteractionService.shapeColor(this.selectedColor);
            }
        });
        this.componentInteractionService.updateSelectedColor(this.selectedColor);
        const selectedObject = this.componentInteractionService.meshSelections.length;
        const allmeshesInScene = this.scenemanagerService.getMeshes();
        if (this.interactionService.checkDuplicateOverlap(allmeshesInScene)) {
            return;
        }
        if (selectedObject) {
            const selectedOption = await this.openDialog();
            this.applySelectedColorToSelectedMeshes(selectedOption);
        }
    }

    private applySelectedColorToSelectedMeshes(selectedOption: string): void {
        const action: UndoRedoAction = {
            actionType: Action.Edit,
            selectedOption: selectedOption,
            meshStates: this.selectionList.map((selection): MeshState => {
                const selectedMesh = selection.mesh;

                this.meshVisualService.storeOriginalColors(selectedMesh);
                const originalColors = [...selectedMesh.userData[Object3DUserData.originalColors]];

                return {
                    mesh: selectedMesh,
                    previousOriginalColors: originalColors,
                };
            }),
        };

        this.undoRedoService.addToUndo(action);

        this.selectionList.forEach((selection) => {
            const selectedMesh = selection.mesh;
            const materialIndex = selection.materialIndex;

            if (materialIndex === undefined) {
                this.openSnackBar(StructureResponseMessage.ClickingOutline, SnackBarConfig.Actions.OKAY);
            }

            this.meshVisualService.applyColorToMesh(selectedMesh, this.selectedColor, selectedOption, materialIndex);
        });

        this.interactionService.markAsEdited();
        this.interactionService.deselectAllMeshes(true);
        this.interactionService.render();
    }

    private openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action, {
            ...SnackBarConfig.Defaults,
        });
    }

    private openDialog(): Promise<string> {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.ColorCustomization,
                title: "Choose Option",
                inputs: [
                    {
                        controlName: "faceOption",
                        type: "radio",
                        options: [
                            { value: "singleFace", label: "Single Face" },
                            { value: "fullFaces", label: "Full Faces" },
                        ],
                    },
                ],
                firstBtn: "Confirm",
                secondBtn: "Cancel",
            },
            autoFocus: false,
            restoreFocus: false,
        });

        // Return a promise that resolves when the dialog closes
        return new Promise((resolve) => {
            dialogRef.componentInstance.saveClicked.subscribe((selectedData: any) => {
                dialogRef.close();
                resolve(selectedData);
            });
        });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }
}
